import { useState, useEffect, useCallback } from 'react';
import { initAnalytics, trackEvent } from '@/Services/analytics';

const CONSENT_KEY = 'cookie_consent_v1';

interface UseCookieConsentReturn {
    consented: boolean | null;
    accept: () => void;
    dismiss: () => void;
}

/**
 * Manages cookie consent state.
 * Returns `consented` as:
 *   - `null` when no choice has been made yet (banner should show)
 *   - `true` when the user accepted
 *   - `false` when the user rejected
 */
export function useCookieConsent(): UseCookieConsentReturn {
    const [consented, setConsented] = useState<boolean | null>(null);

    useEffect(() => {
        let stored: string | null = null;
        try {
            stored = window.localStorage.getItem(CONSENT_KEY);
        } catch {
            // storage unavailable
        }
        if (stored === 'accepted') {
            setConsented(true);
        } else if (stored === 'rejected') {
            setConsented(false);
        } else {
            setConsented(null);
        }

        const onReopen = () => {
            setConsented(null);
        };
        window.addEventListener('bk:reopen-consent', onReopen);
        return () => window.removeEventListener('bk:reopen-consent', onReopen);
    }, []);

    const saveChoice = useCallback((value: string) => {
        try {
            window.localStorage.setItem(CONSENT_KEY, value);
        } catch {
            // storage unavailable
        }
        trackEvent('cookie_consent_' + value);
        if (value === 'accepted') {
            initAnalytics();
        }
        setConsented(value === 'accepted');
    }, []);

    const accept = useCallback(() => {
        saveChoice('accepted');
    }, [saveChoice]);

    const dismiss = useCallback(() => {
        saveChoice('rejected');
    }, [saveChoice]);

    return { consented, accept, dismiss };
}
